import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  Paper,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNewsNotifications } from '../hooks/use-news-notifications';

import { NotificationCenterDialog } from './notification-center-dialog';
import { NotificationList } from './notification-list';

const DROPDOWN_MAX = 10;

export const NotificationBell = () => {
  const { t } = useTranslation();
  const { bellNotifications, unseenBellCount, markAllSeen } = useNewsNotifications();

  const anchorRef = useRef<HTMLButtonElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleBellClick = useCallback(() => {
    setPopoverOpen((prev) => !prev);
    if (!popoverOpen) markAllSeen();
  }, [popoverOpen, markAllSeen]);

  const handleClose = useCallback(() => {
    setPopoverOpen(false);
  }, []);

  const handleViewAll = useCallback(() => {
    setPopoverOpen(false);
    setDialogOpen(true);
  }, []);

  return (
    <>
      <Tooltip title={t('notifications.bell.tooltip')}>
        <IconButton ref={anchorRef} onClick={handleBellClick} color="inherit" size="small">
          <Badge badgeContent={unseenBellCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={popoverOpen}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 500 } } }}
      >
        <Paper elevation={0}>
          <Box px={2} py={1.5} display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{t('notifications.bell.title')}</Typography>
            {bellNotifications.length > DROPDOWN_MAX && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ cursor: 'pointer' }}
                onClick={handleViewAll}
              >
                {t('notifications.bell.viewAll')}
              </Typography>
            )}
          </Box>
          <Divider />
          <NotificationList notifications={bellNotifications} maxItems={DROPDOWN_MAX} />
        </Paper>
      </Popover>

      <NotificationCenterDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
      />
    </>
  );
};
